import { getMomentTz } from "@dexteel/mesf-core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

import { SectionDot } from "@/shared/selectors/ProductionEventSelector";
import type { TestShackReportSectionRevBase } from "@/pages/products/test-shack/models/TestShackReportSectionRev";
import {
	formatOutOfSpec,
	formatRevision,
} from "@/pages/products/test-shack/utils/reportFormat";
import { TestShackReportNoSpecificationLegend } from "./TestShackReportNoSpec";

const moment = getMomentTz();

interface TestShackReportSectionRevCardProps {
	sectionRev: TestShackReportSectionRevBase;
	isCollapsed: boolean;
	onToggle: () => void;
	children: ReactNode;
	/** Notes about the whole Section-Rev, shown under its body. */
	footer?: ReactNode;
}

const formatPeriod = (start: Date, end: Date) => {
	const from = moment.utc(start).format("MM/DD/YY");
	const to = moment.utc(end).format("MM/DD/YY");
	return from === to ? from : `${from} – ${to}`;
};

export const TestShackReportSectionRevCard = ({
	sectionRev,
	isCollapsed,
	onToggle,
	children,
	footer,
}: TestShackReportSectionRevCardProps) => {
	const inspectionCount = sectionRev.rows.length;
	const hasIssues = sectionRev.hasTolerances && sectionRev.outOfSpecCount > 0;
	const headerId = `test-shack-section-rev-${sectionRev.key}`;
	const bodyId = `${headerId}-body`;
	const footerContent = sectionRev.hasTolerances ? (
		footer
	) : (
		<TestShackReportNoSpecificationLegend />
	);

	return (
		<Paper elevation={1} data-test-id={`report-section-rev-${sectionRev.key}`}>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				onClick={onToggle}
				sx={{
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
					borderBottom: isCollapsed ? 0 : 1,
					borderColor: "divider",
					borderTopLeftRadius: "inherit",
					borderTopRightRadius: "inherit",
					borderBottomLeftRadius: isCollapsed ? "inherit" : 0,
					borderBottomRightRadius: isCollapsed ? "inherit" : 0,
					cursor: "pointer",
					px: 1,
					py: 1,
				}}
			>
				<IconButton
					size="small"
					id={headerId}
					aria-expanded={!isCollapsed}
					aria-controls={bodyId}
					aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${sectionRev.sectionName}`}
					sx={{
						transform: isCollapsed ? "rotate(-90deg)" : "none",
						transition: "transform 150ms",
					}}
				>
					<ExpandMoreIcon fontSize="small" />
				</IconButton>
				<SectionDot sectionId={sectionRev.sectionId} />
				<Typography variant="h6" fontWeight={800}>
					{sectionRev.sectionName}
				</Typography>
				<Chip
					size="small"
					label={formatRevision(sectionRev.specRev, sectionRev.specSubRev)}
					color="primary"
					variant="outlined"
					sx={{ fontSize: "11px", fontWeight: 700, height: 20 }}
				/>
				<Typography color="text.secondary" sx={{ fontSize: "13px" }}>
					{inspectionCount === 1
						? "1 inspection"
						: `${inspectionCount} inspections`}
				</Typography>
				<Typography color="text.secondary" sx={{ fontSize: "13px" }}>
					·{" "}
					{formatPeriod(
						sectionRev.firstProductionDate,
						sectionRev.lastProductionDate,
					)}
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Chip
					size="small"
					label={formatOutOfSpec(sectionRev)}
					color={
						hasIssues
							? "error"
							: sectionRev.hasTolerances
								? "success"
								: "warning"
					}
					variant={
						hasIssues || !sectionRev.hasTolerances ? "filled" : "outlined"
					}
					sx={{ fontSize: "11px", fontWeight: 700, height: 22 }}
				/>
			</Stack>

			<Collapse in={!isCollapsed} unmountOnExit>
				<Stack
					id={bodyId}
					role="region"
					aria-labelledby={headerId}
					direction={{ xs: "column", lg: "row" }}
					spacing={1.5}
					alignItems="flex-start"
					sx={{ p: 1.5 }}
				>
					{children}
				</Stack>
				{footerContent && (
					<Box
						sx={{
							borderTop: 1,
							borderColor: "divider",
							px: 1.5,
							py: 0.75,
						}}
					>
						{footerContent}
					</Box>
				)}
			</Collapse>
		</Paper>
	);
};
